import {generateUUID} from '../../../../utility/uuidUtil';

// AD-2691 (Maria, 14 October 2026): up to three components can sit side by side on a row. A row is the run of
// components next to each other in the form that share layout.rowId; each one's layout.width is its share of
// the row as a percentage. A component without a layout has a row of its own, as every form had before.

export const MAX_PER_ROW = 3;
export const MIN_WIDTH = 15;

const rowIdOf = (component) => component?.layout?.rowId ?? null;

// The form's components grouped into rows, keeping each component's index in the form.
export const groupIntoRows = (schema = []) => {
    const rows = [];
    const keys = new Set();
    schema.forEach((field, index) => {
        if (field?.type === 'schoolLogo') return;
        const rowId = rowIdOf(field);
        const last = rows[rows.length - 1];
        if (rowId && last && last.rowId === rowId) {
            last.items.push({field, index});
        } else {
            // Each row needs its own key for dragging, even if a row id were ever split in two.
            const key = rowId && !keys.has(rowId) ? rowId : String(field.id);
            keys.add(key);
            rows.push({rowId, key, items: [{field, index}]});
        }
    });
    return rows;
};

const equalWidth = (count) => Math.round((100 / count) * 10) / 10;

// Give every component on the row the same width (50% for two, 33.3% for three), or take the layout off a
// component left on its own.
const equalise = (schema, rowId) => {
    const members = schema.filter(component => rowIdOf(component) === rowId);
    return schema.map(component => {
        if (rowIdOf(component) !== rowId) return component;
        if (members.length < 2) {
            const {layout, ...rest} = component;
            return rest;
        }
        return {...component, layout: {rowId, width: equalWidth(members.length)}};
    });
};

// After a component leaves a row or is deleted, the others keep their proportions and fill the row again.
// A component left on its own has its layout taken off.
export const tidyRow = (schema, rowId) => {
    if (!rowId) return schema;
    const members = schema.filter(component => rowIdOf(component) === rowId);
    const total = members.reduce((sum, component) => sum + (component.layout.width || 0), 0);
    return schema.map(component => {
        if (rowIdOf(component) !== rowId) return component;
        if (members.length < 2) {
            const {layout, ...rest} = component;
            return rest;
        }
        const width = total > 0
            ? Math.round((component.layout.width / total) * 1000) / 10
            : equalWidth(members.length);
        return {...component, layout: {rowId, width}};
    });
};

// The components of a row, in order, from the component ids.
const membersOf = (schema, rowId, fallbackId) => rowId
    ? schema.filter(component => rowIdOf(component) === rowId)
    : schema.filter(component => component.id === fallbackId);

// Put the component with movingId on the row of the component with targetId, at the end of that row. Returns
// the schema unchanged when the row already has three components.
export const joinRow = (schema, movingId, targetId) => {
    if (movingId === targetId) return schema;
    const target = schema.find(component => component.id === targetId);
    const moving = schema.find(component => component.id === movingId);
    if (!target || !moving) return schema;

    const targetRowId = rowIdOf(target);
    const targetMembers = membersOf(schema, targetRowId, targetId);
    if (targetMembers.length >= MAX_PER_ROW) return schema;

    const oldRowId = rowIdOf(moving);
    const rowId = targetRowId || generateUUID();

    // Take the moving component out, then put it after the last component of the target row.
    let next = schema.filter(component => component.id !== movingId)
        .map(component => (component.id === targetId && !targetRowId)
            ? {...component, layout: {rowId, width: 50}}
            : component);
    const lastMemberId = targetMembers[targetMembers.length - 1].id;
    const at = next.findIndex(component => component.id === lastMemberId) + 1;
    next = [...next.slice(0, at), {...moving, layout: {rowId, width: 50}}, ...next.slice(at)];

    next = equalise(next, rowId);
    if (oldRowId && oldRowId !== rowId) next = equalise(next, oldRowId);
    return next;
};

// Take a component off its row and put it on its own row straight after the row it was on.
export const leaveRow = (schema, componentId) => {
    const component = schema.find(item => item.id === componentId);
    const rowId = rowIdOf(component);
    if (!rowId) return schema;

    const members = membersOf(schema, rowId);
    const lastMemberId = members[members.length - 1].id;
    const {layout, ...alone} = component;

    let next;
    if (lastMemberId === componentId) {
        // It was last on the row, so it stays where it is, on a row of its own.
        next = schema.map(item => item.id === componentId ? alone : item);
    } else {
        next = schema.filter(item => item.id !== componentId);
        const at = next.findIndex(item => item.id === lastMemberId) + 1;
        next = [...next.slice(0, at), alone, ...next.slice(at)];
    }
    // AD-2691 (staging test, 15 October 2026): the components left on the row share the width equally again.
    return equalise(next, rowId);
};

// Move the border between two neighbours on a row. leftId's width becomes leftWidth, and the right-hand
// neighbour takes up the difference, with neither narrower than MIN_WIDTH.
export const resizePair = (schema, leftId, rightId, leftWidth) => {
    const left = schema.find(item => item.id === leftId);
    const right = schema.find(item => item.id === rightId);
    if (!left?.layout || !right?.layout) return schema;
    const total = left.layout.width + right.layout.width;
    const nextLeft = Math.round(Math.min(total - MIN_WIDTH, Math.max(MIN_WIDTH, leftWidth)) * 10) / 10;
    const nextRight = Math.round((total - nextLeft) * 10) / 10;
    return schema.map(item => {
        if (item.id === leftId) return {...item, layout: {...item.layout, width: nextLeft}};
        if (item.id === rightId) return {...item, layout: {...item.layout, width: nextRight}};
        return item;
    });
};

// A copy of a component goes on its own row, straight after the row of the component copied.
export const placeCopy = (schema, sourceId, copy) => {
    const source = schema.find(item => item.id === sourceId);
    const {layout, ...alone} = copy;
    const rowId = rowIdOf(source);
    const members = membersOf(schema, rowId, sourceId);
    const at = schema.findIndex(item => item.id === members[members.length - 1].id) + 1;
    return [...schema.slice(0, at), alone, ...schema.slice(at)];
};

// Move a whole row so it starts at form position toRowIndex (counted in rows).
export const moveRow = (schema, fromRowIndex, toRowIndex) => {
    const rows = groupIntoRows(schema);
    if (fromRowIndex === toRowIndex || !rows[fromRowIndex]) return schema;
    const [moved] = rows.splice(fromRowIndex, 1);
    rows.splice(toRowIndex, 0, moved);
    const logo = schema.filter(component => component?.type === 'schoolLogo');
    return [...rows.flatMap(row => row.items.map(item => item.field)), ...logo];
};

// The form position (index in the schema) at which a new component dropped before row rowIndex goes.
export const indexBeforeRow = (schema, rowIndex) => {
    const rows = groupIntoRows(schema);
    if (rowIndex >= rows.length) {
        return schema.filter(component => component?.type !== 'schoolLogo').length;
    }
    return rows[rowIndex].items[0].index;
};
